<script lang="ts">
  import { onMount, onDestroy } from "svelte"
  import Layout from "$app/Layouts/Plan.svelte"
  import { slide } from "svelte/transition"
  import Modal from "$app/components/Modal.svelte"
  import Post from "../Community/Post.svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import { xhr } from "$app/utils"
  import { CHANNEL_ICONS } from "$app/utils/channels"
  import { ChevronDown, ChevronLeft } from "lucide-svelte"
  import logo from "@images/logo/logo-mark-white.png"
  import { handleVideoLinkClick } from "$app/utils"
  import { appStore } from "$app/store.svelte"

  interface Props {
    plan: PlanType
    current_user: UserType
    channel_permission_level?: string
    top_channels?: ForumChannelType[]
    public_channels?: ForumChannelType[]
    private_channels?: ForumChannelType[]
    current_channel?: ForumChannelType
    members?: UserType[]
    post?: ForumPostType
    notification_id?: number
  }

  let props = $props()
  let {
    plan,
    current_user,
    channel_permission_level = "none",
    top_channels = [],
    public_channels = [],
    private_channels = [],
    members = [],
    notification_id = null
  }: Props = props

  let current_channel = $state(props.current_channel || null)
  let post = $state(props.post || null)

  appStore.current_user = current_user
  appStore.plan = plan
  appStore.permission_level = channel_permission_level

  function updateChannel(channel) {
    current_channel = channel
    window.history.replaceState(
      { channel: channel.slug },
      "Community",
      `/plans/${plan.id}/community?channel=${channel.slug}`
    )
    window.location.pathname = `/plans/${plan.id}/community`
  }

  let savingTerms = $state(false)
  async function acceptTerms(): Promise<void> {
    savingTerms = true
    try {
      const resp = await xhr.put(`/users/${current_user.id}.json`, {
        user: { has_accepted_community_terms: true }
      })
      savingTerms = false
      current_user = resp.data
    } catch (e) {
      Honeybadger.notify(e)
    } finally {
      savingTerms = false
      openTermsModal = false
    }
  }

  let openTermsModal = $state(!current_user.has_accepted_community_terms)

  let showPublicChannels = $state(true)
  let showPrivateChannels = $state(true)

  function deletePost() {
    updateChannel(current_channel)
  }

  function updatePost(event) {
    post = event.detail
  }

  if (notification_id) {
    loadNotification()
  }

  async function loadNotification(): Promise<void> {
    try {
      const resp = await xhr.get(`/api/v1/notifications/${notification_id}`)
      let notification = resp.data.notification
      if (notification.type === "ForumPostCommentNotifier") {
        post.comments = post.comments.map((c) => {
          if (notification.params.forum_post_comment.id === c.id) {
            return { ...c, _marked: true }
          } else {
            return { ...c, _marked: false }
          }
        })
      }
    } catch (e) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
    }
  }

  onMount(() => {
    let postsElement = document.addEventListener(
      "click",
      handleVideoLinkClick,
      { passive: false }
    )
  })
  onDestroy(() => {
    document.removeEventListener("click", handleVideoLinkClick)
  })
</script>

<Layout {...props}>
  <div class="border-t border-indigo-100 flex items-stretch min-h-screen">
    <div class="w-full flex">
      <div class="hidden lg:w-108 lg:block bg-indigo-800 sticky top-4">
        <div
          class="border-b border-indigo-400 mb-3 flex py-4 items-center px-4"
        >
          <img src={logo} class="w-8 h-8 shadow-lg" alt="LWP Logo" />
          <div class="text-gray-100 text-2xl ml-2">Community</div>
        </div>
        <nav
          aria-label="Sidebar"
          class="sticky top-4 divide-y divide-gray-300 p-2"
        >
          <div class="space-y-1 pb-8">
            {#each top_channels as channel}
              <button
                onclick={() => updateChannel(channel)}
                class="hover:text-gray-100 hover:bg-indigo-700 group flex items-center rounded-md px-3 py-2 text-sm font-medium w-full"
                class:bg-indigo-600={current_channel.id == channel.id}
                class:text-indigo-200={current_channel.id == channel.id}
                class:text-white={current_channel.id !== channel.id}
              >
                {#if channel.icon in CHANNEL_ICONS}
                  {@const Icon = CHANNEL_ICONS[channel.icon]}
                  <Icon
                    src={CHANNEL_ICONS[channel.icon]}
                    class="-ml-1 mr-3 size-6 flex-shrink-0"
                  />
                {/if}
                <span class="truncate">{channel.name}</span>
              </button>
            {/each}
          </div>
          <div class="pt-6 pb-8">
            <button
              class="px-3 text-base font-bold text-gray-100 flex items-center hover:text-indigo-200 w-full py-2"
              id="public-channels"
              onclick={() => (showPublicChannels = !showPublicChannels)}
            >
              <ChevronDown
                class="size-5 {!showPublicChannels && 'rotate-270'}"
              />
              Public Channels
            </button>
            {#if showPublicChannels}
              <div class="mt-3 space-y-2" transition:slide>
                {#each public_channels as channel}
                  <button
                    onclick={() => updateChannel(channel)}
                    class="hover:bg-indigo-700 group flex items-center rounded-md px-3 py-2 text-sm font-medium w-full hover:text-gray-100"
                    class:bg-indigo-600={current_channel.id == channel.id}
                    class:text-indigo-200={current_channel.id == channel.id}
                    class:text-white={current_channel.id !== channel.id}
                  >
                    {#if channel.icon in CHANNEL_ICONS}
                      {@const Icon = CHANNEL_ICONS[channel.icon]}
                      <Icon
                        src={CHANNEL_ICONS[channel.icon]}
                        class="-ml-1 mr-3 size-6 flex-shrink-0"
                      />
                    {/if}
                    <span class="truncate">{channel.name}</span>
                  </button>
                {/each}
              </div>
            {/if}
          </div>
          {#if private_channels.length > 0}
            <div class="pt-6">
              <button
                class="px-3 text-base font-bold text-gray-100 flex items-center hover:text-indigo-200 w-full py-2"
                id="private-channels"
                onclick={() => (showPrivateChannels = !showPrivateChannels)}
              >
                <ChevronDown
                  class="size-5 {!showPrivateChannels && 'rotate-270'}"
                />
                Private Channels
              </button>
              {#if showPrivateChannels}
                <div class="mt-3 space-y-2" transition:slide>
                  {#each private_channels as channel}
                    <button
                      onclick={() => updateChannel(channel)}
                      class="hover:text-gray-100 hover:bg-indigo-700 group w-full flex items-center rounded-md px-3 py-2 text-sm font-medium"
                      class:bg-indigo-600={current_channel.id == channel.id}
                      class:text-indigo-200={current_channel.id == channel.id}
                      class:text-white={current_channel.id !== channel.id}
                    >
                      {#if channel.icon in CHANNEL_ICONS}
                        {@const Icon = CHANNEL_ICONS[channel.icon]}
                        <Icon
                          src={CHANNEL_ICONS[channel.icon]}
                          class="-ml-1 mr-3 size-6 flex-shrink-0"
                        />
                      {/if}
                      <span class="truncate">{channel.name}</span>
                    </button>
                  {/each}
                </div>
              {/if}
            </div>
          {/if}
        </nav>
      </div>
      <main class="w-full bg-lwp-pale-grey px-8 lg:px-12 py-6">
        <div
          class="bg-white pt-4 pb-4 px-6 sticky top-0 border-b border-indigo-100 z-20 shadow-lg rounded"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <h1 class="text-3xl font-bold">{current_channel.name}</h1>
              {#if members.length > 0}
                <span class="text-sm font-bold ml-4">MEMBERS</span>
                <div class="ml-1 isolate flex -space-x-2 overflow-hidden">
                  {#each members.slice(0, 10) as member}
                    <img
                      class="relative z-30 inline-block h-8 w-8 rounded-full ring-2 ring-white"
                      src={member.avatar_thumb_url}
                      alt={member.name}
                    />
                  {/each}
                </div>
                {#if members.length > 10}
                  <span class="ml-1 text-base font-bold p-1"
                    >+{members.length - 9}</span
                  >
                {/if}
              {/if}
            </div>
            <button
              onclick={() => updateChannel(current_channel)}
              class="flex items-center bg-blue-200 py-2 px-4 rounded hover:shadow hover:bg-blue-400 hover:text-white text-blue-700 border border-blue-400"
            >
              <ChevronLeft class="size-5 mr-2" />
              View All Channel Posts
            </button>
          </div>
        </div>

        <ul role="list" class="mt-6">
          <Post
            {post}
            showPinned={false}
            showComments={true}
            ondelete={deletePost}
            onupdate={updatePost}
          />
        </ul>
      </main>
    </div>
  </div>
</Layout>

<Modal
  bind:open={openTermsModal}
  closeButton={true}
  closeOnEsc={false}
  closeOnOuterClick={false}
  large={true}
>
  <div
    class="border-b border-indigo-800 flex py-4 items-center justify-center px-4 bg-indigo-600 mx-5 rounded-tl-lg rounded-tr-lg"
  >
    <img src={logo} class="w-8 h-8 shadow-lg" alt="Lead With Purpose" />
    <div class="text-gray-100 text-2xl ml-2">
      Welcome to the Lead With Purpose Community
    </div>
  </div>

  <div class="mx-4 py-4 bg-gray-200">
    <p class="mt-2 text-center text-xl font-bold">Community Terms of Use</p>
    <div class="h-80 overflow-y-auto border border-gray-400 rounded m-2 py-4">
      <p class="px-4">
        By participating in the Lead With Purpose community you will be
        connecting with the larger Lead With Purpose network beyond your team or
        company. Any posts or comments made here in the public forums will be
        viewable by all users of the Lead With Purpose platform. All posts must
        abide by the Lead With Purpose Community terms and any that fail to meet
        our guidelines will be moderated.
      </p>
      <p class="px-4 mt-4">
        When you use Lead With Purpose, you join a community of people from all
        over the world. The guidelines below help keep the Lead With Purpose
        Community fun and enjoyable for everyone. We want to help foster
        meaningful and genuine interactions on how you and your companies are
        leading purposeful lives with the employees and clients you serve.
      </p>
      <p class="px-4 mt-4">
        We created the Community Guidelines so you can help us foster and
        protect this amazing community. By using the Lead With Purpose
        Community, you agree to these guidelines and our Terms of Use. We’re
        committed to these guidelines and we hope you are too. Overstepping
        these boundaries may result in deleted content or other restrictions.
      </p>
      <p class="px-4 mt-4">
        The Lead With Purpose Community is not a place to support or praise
        terrorism, organized crime, or hate groups. Offering sexual services,
        buying or selling firearms, alcohol, and tobacco products between
        private individuals, and buying or selling non-medical or pharmaceutical
        drugs are also not allowed. We also remove content that attempts to
        trade, co-ordinate the trade of, donate, gift, or ask for non-medical
        drugs, as well as content that either admits to personal use (unless in
        the recovery context) or coordinates or promotes the use of non-medical
        drugs. Instagram also prohibits the sale of live animals between private
        individuals, though brick-and-mortar stores may offer these sales. No
        one may coordinate poaching or selling of endangered species or their
        parts.
      </p>
      <p class="px-4 mt-4">
        We have zero tolerance when it comes to sharing sexual content involving
        minors or threatening to post intimate images of others.
      </p>
      <p class="px-4 mt-4">
        We want to foster a positive, diverse community. We remove content that
        contains credible threats or hate speech, content that targets private
        individuals to degrade or shame them, personal information meant to
        blackmail or harass someone, and repeated unwanted messages.
      </p>
      <p class="px-4 mt-4">
        Thank you for your help to maintain a respectful Lead With Purpose
        Community where we can learn together.
      </p>
    </div>
  </div>
  <div
    class="flex justify-center mx-4 py-4 bg-gray-700 rounded-bl-lg rounded-br-lg"
  >
    {#if savingTerms}
      <button class="px-4 py-2 bg-green-900 text-lg rounded-lg text-white">
        Saving...
      </button>
    {:else}
      <button
        class="px-4 py-2 bg-green-700 text-lg rounded-lg text-white"
        onclick={acceptTerms}
      >
        I agree to abide by the terms
      </button>
    {/if}
  </div>
</Modal>
